import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import plansApi, { Plan, PlanAddon } from "../../api/plans";
import { uploadApi } from "../../api/upload";

interface PlanModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  plan: Plan | null;
}

const PlanModal: React.FC<PlanModalProps> = ({ open, onClose, onSaved, plan }) => {
  const isEditing = Boolean(plan);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [duration, setDuration] = useState(28);
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [badgeText, setBadgeText] = useState("");
  const [planType, setPlanType] = useState<"meal" | "drink" | "meal_and_drink">("meal");
  const [isActive, setIsActive] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [ifPortionsAvailable, setIfPortionsAvailable] = useState(true);
  const [isBestseller, setIsBestseller] = useState(false);
  const [calorieRangeClassic, setCalorieRangeClassic] = useState("500–650 kcal");
  const [calorieRangeLight, setCalorieRangeLight] = useState("350–500 kcal");

  // Plan Add-ons
  const [addOns, setAddOns] = useState<PlanAddon[]>([]);
  const [isAddingAddon, setIsAddingAddon] = useState(false);
  const [editingAddonIndex, setEditingAddonIndex] = useState<number | null>(null);
  const [addonName, setAddonName] = useState("");
  const [addonDescription, setAddonDescription] = useState("");
  const [addonPrice, setAddonPrice] = useState<number | "">("");
  const [addonQuantity, setAddonQuantity] = useState("");
  const [addonIsActive, setAddonIsActive] = useState(true);
  const [addonImage, setAddonImage] = useState("");
  const [uploadingAddonImage, setUploadingAddonImage] = useState(false);

  // Meal Prices
  const [price, setPrice] = useState<number>(248);
  const [priceWeekly, setPriceWeekly] = useState<number>(218);
  const [priceMonthly, setPriceMonthly] = useState<number>(188);
  const [priceNonOffer, setPriceNonOffer] = useState<number>(248);
  const [priceLight, setPriceLight] = useState<number>(217);
  const [priceWeeklyLight, setPriceWeeklyLight] = useState<number>(197);
  const [priceMonthlyLight, setPriceMonthlyLight] = useState<number>(167);
  const [priceNonOfferLight, setPriceNonOfferLight] = useState<number>(217);

  // Drink Pricing Tiers
  const [coffeeTrial, setCoffeeTrial] = useState<number>(170);
  const [coffeeWeekly, setCoffeeWeekly] = useState<number>(150);
  const [coffeeMonthly, setCoffeeMonthly] = useState<number>(130);
  const [coffeeNonOffer, setCoffeeNonOffer] = useState<number>(170);

  const [blendTrial, setBlendTrial] = useState<number>(200);
  const [blendWeekly, setBlendWeekly] = useState<number>(180);
  const [blendMonthly, setBlendMonthly] = useState<number>(160);
  const [blendNonOffer, setBlendNonOffer] = useState<number>(200);

  // Features list
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeatureText, setNewFeatureText] = useState("");

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingImage(true);
    setError(null);
    try {
      const response = await uploadApi.uploadImage(file, "plans");
      setImage(response.imageUrl);
    } catch (err: any) {
      console.error(err);
      setError("Failed to upload plan image. Please try again or paste image URL.");
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    if (plan) {
      setTitle(plan.title || "");
      setDescription(plan.description || "");
      setImage(plan.image || "");
      setDuration(plan.duration || 28);
      setSortOrder(plan.sortOrder ?? 1);
      setBadgeText(plan.badgeText || "");
      setPlanType(plan.planType || "meal");
      setIsActive(plan.isActive ?? true);
      setIsEnabled(plan.isEnabled ?? true);
      setIfPortionsAvailable(plan.ifPortionsAvailable ?? true);
      setIsBestseller(plan.isBestseller ?? false);
      setCalorieRangeClassic(plan.calorieRangeClassic || "500–650 kcal");
      setCalorieRangeLight(plan.calorieRangeLight || "350–500 kcal");

      setPrice(plan.price ?? 248);
      setPriceWeekly(plan.priceWeekly ?? 218);
      setPriceMonthly(plan.priceMonthly ?? 188);
      setPriceNonOffer(plan.priceNonOffer ?? 248);
      setPriceLight(plan.priceLight ?? 217);
      setPriceWeeklyLight(plan.priceWeeklyLight ?? 197);
      setPriceMonthlyLight(plan.priceMonthlyLight ?? 167);
      setPriceNonOfferLight(plan.priceNonOfferLight ?? 217);

      setCoffeeTrial(plan.drinkPricing?.coffee?.priceTrial ?? 170);
      setCoffeeWeekly(plan.drinkPricing?.coffee?.priceWeekly ?? 150);
      setCoffeeMonthly(plan.drinkPricing?.coffee?.priceMonthly ?? 130);
      setCoffeeNonOffer(plan.drinkPricing?.coffee?.priceNonOffer ?? 170);

      setBlendTrial(plan.drinkPricing?.blend?.priceTrial ?? 200);
      setBlendWeekly(plan.drinkPricing?.blend?.priceWeekly ?? 180);
      setBlendMonthly(plan.drinkPricing?.blend?.priceMonthly ?? 160);
      setBlendNonOffer(plan.drinkPricing?.blend?.priceNonOffer ?? 200);

      setFeatures(Array.isArray(plan.features) ? plan.features : []);
      setAddOns(Array.isArray(plan.addOns) ? [...plan.addOns] : []);
    } else {
      // Default reset for new plan
      setTitle("");
      setDescription("");
      setImage("");
      setDuration(28);
      setSortOrder(1);
      setBadgeText("");
      setPlanType("meal");
      setIsActive(false); // Default to draft for new plans
      setIsEnabled(true);
      setIfPortionsAvailable(true);
      setIsBestseller(false);
      setCalorieRangeClassic("500–650 kcal");
      setCalorieRangeLight("350–500 kcal");

      setPrice(248);
      setPriceWeekly(218);
      setPriceMonthly(188);
      setPriceNonOffer(248);
      setPriceLight(217);
      setPriceWeeklyLight(197);
      setPriceMonthlyLight(167);
      setPriceNonOfferLight(217);

      setCoffeeTrial(170);
      setCoffeeWeekly(150);
      setCoffeeMonthly(130);
      setCoffeeNonOffer(170);

      setBlendTrial(200);
      setBlendWeekly(180);
      setBlendMonthly(160);
      setBlendNonOffer(200);

      setFeatures([]);
      setAddOns([]);
    }
    setIsAddingAddon(false);
    setEditingAddonIndex(null);
    setError(null);
  }, [plan, open]);

  const handleAddFeature = () => {
    if (!newFeatureText.trim()) return;
    setFeatures((prev) => [...prev, newFeatureText.trim()]);
    setNewFeatureText("");
  };

  const handleRemoveFeature = (idx: number) => {
    setFeatures((prev) => prev.filter((_, i) => i !== idx));
  };

  // Add-on management handlers
  const handleOpenAddAddon = () => {
    setEditingAddonIndex(null);
    setAddonName("");
    setAddonDescription("");
    setAddonPrice("");
    setAddonQuantity("");
    setAddonIsActive(true);
    setAddonImage("");
    setIsAddingAddon(true);
  };

  const handleOpenEditAddon = (index: number) => {
    const item = addOns[index];
    if (!item) return;
    setEditingAddonIndex(index);
    setAddonName(item.name || "");
    setAddonDescription(item.description || "");
    setAddonPrice(item.price ?? "");
    setAddonQuantity(item.quantity || "");
    setAddonIsActive(item.isActive ?? true);
    setAddonImage(item.image || "");
    setIsAddingAddon(true);
  };

  const handleCloseAddonEditor = () => {
    setIsAddingAddon(false);
    setEditingAddonIndex(null);
  };

  const handleSaveAddon = () => {
    if (!addonName.trim()) {
      setError("Add-on Name is required");
      return;
    }
    const priceNum = Number(addonPrice);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Please enter a valid price for the add-on");
      return;
    }

    const newAddon: PlanAddon = {
      name: addonName.trim(),
      description: addonDescription.trim(),
      price: priceNum,
      quantity: addonQuantity.trim(),
      isActive: addonIsActive,
      image: addonImage.trim(),
    };

    if (editingAddonIndex !== null && editingAddonIndex >= 0) {
      const updated = [...addOns];
      updated[editingAddonIndex] = { ...updated[editingAddonIndex], ...newAddon };
      setAddOns(updated);
    } else {
      setAddOns([...addOns, newAddon]);
    }
    handleCloseAddonEditor();
  };

  const handleDeleteAddon = (index: number) => {
    setAddOns(addOns.filter((_, idx) => idx !== index));
    if (editingAddonIndex === index) {
      handleCloseAddonEditor();
    }
  };

  const handleToggleAddonActive = (index: number) => {
    const updated = [...addOns];
    updated[index] = { ...updated[index], isActive: !updated[index].isActive };
    setAddOns(updated);
  };

  const handleUploadAddonImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingAddonImage(true);
    try {
      const res = await uploadApi.uploadImage(file, "addons");
      setAddonImage(res.imageUrl);
    } catch (err: any) {
      console.error(err);
      setError("Failed to upload add-on image");
    } finally {
      setUploadingAddonImage(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Plan Title is required");
      return;
    }
    if (!description.trim()) {
      setError("Plan Description / Tagline is required");
      return;
    }

    setSaving(true);
    setError(null);

    const payload: Partial<Plan> = {
      title: title.trim(),
      description: description.trim(),
      image: image.trim(),
      duration: Number(duration) || 28,
      sortOrder: Number(sortOrder) || 0,
      badgeText: badgeText.trim(),
      planType,
      isActive,
      isEnabled,
      ifPortionsAvailable: planType === "drink" ? false : ifPortionsAvailable,
      isBestseller,
      calorieRangeClassic: calorieRangeClassic.trim(),
      calorieRangeLight: calorieRangeLight.trim(),
      price: Number(price),
      priceWeekly: Number(priceWeekly),
      priceMonthly: Number(priceMonthly),
      priceNonOffer: Number(priceNonOffer),
      priceLight: Number(priceLight),
      priceWeeklyLight: Number(priceWeeklyLight),
      priceMonthlyLight: Number(priceMonthlyLight),
      priceNonOfferLight: Number(priceNonOfferLight),
      features,
      addOns,
    };

    if (planType === "drink" || planType === "meal_and_drink") {
      payload.drinkPricing = {
        coffee: {
          priceTrial: Number(coffeeTrial),
          priceWeekly: Number(coffeeWeekly),
          priceMonthly: Number(coffeeMonthly),
          priceNonOffer: Number(coffeeNonOffer),
        },
        blend: {
          priceTrial: Number(blendTrial),
          priceWeekly: Number(blendWeekly),
          priceMonthly: Number(blendMonthly),
          priceNonOffer: Number(blendNonOffer),
        },
      };
    }

    try {
      if (isEditing && plan) {
        await plansApi.updatePlan(plan._id, payload);
      } else {
        await plansApi.createPlan(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={700}>
          {isEditing ? `Edit Plan: ${plan?.title}` : "Create New Plan"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Section 1: Basic Info */}
        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          1. General Configuration
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              label="Plan Title"
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Kinnow Sips, Kinnow Complete"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plan Type</InputLabel>
              <Select
                value={planType}
                label="Plan Type"
                onChange={(e) => setPlanType(e.target.value as any)}
              >
                <MenuItem value="meal">Meal Plan</MenuItem>
                <MenuItem value="drink">Drink Plan (Pure Sips)</MenuItem>
                <MenuItem value="meal_and_drink">Combo Plan (Meals + Drinks)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Description / Tagline"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short catchy tagline and details shown on the plan card"
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Teaser / Highlight Badge (Sparkle Tag on Card)"
              fullWidth
              size="small"
              value={badgeText}
              onChange={(e) => setBadgeText(e.target.value)}
              placeholder="e.g. Now in Classic & Light portions, 13 Cold Brews & 12 Blends, Fresh Breakfast Bowl & Drink Daily"
              helperText="Dynamic badge pill displayed with a sparkle icon at the top of the plan card in the mobile app."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" fontWeight={700} color="textSecondary" sx={{ mb: 1, display: "block" }}>
                PLAN HEADER IMAGE
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {image && (
                  <Box
                    component="img"
                    src={image}
                    alt="Plan Preview"
                    sx={{
                      width: 70,
                      height: 50,
                      borderRadius: 1.5,
                      objectFit: "cover",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploadingImage ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={uploadingImage}
                  sx={{ textTransform: "none" }}
                >
                  {uploadingImage ? "Uploading..." : image ? "Replace" : "Upload Image"}
                  <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </Button>
                <TextField
                  label="Or Paste Image URL"
                  size="small"
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: 180 }}
                  placeholder="https://images.unsplash.com/..."
                />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              label="Duration (Days)"
              type="number"
              fullWidth
              size="small"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              label="Sort Order"
              type="number"
              fullWidth
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              helperText="Sequence (1, 2, 3...)"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Section 2: Portions & Calories */}
        {planType !== "drink" && (
          <>
            <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
              2. Portions & Calories
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ifPortionsAvailable}
                      onChange={(e) => setIfPortionsAvailable(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Offer Classic & Light Portions"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Classic Portion Calorie Range"
                  fullWidth
                  size="small"
                  value={calorieRangeClassic}
                  onChange={(e) => setCalorieRangeClassic(e.target.value)}
                  placeholder="500–650 kcal"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Light Portion Calorie Range"
                  fullWidth
                  size="small"
                  value={calorieRangeLight}
                  onChange={(e) => setCalorieRangeLight(e.target.value)}
                  placeholder="350–500 kcal"
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Section 3: Pricing Matrix */}
        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          {planType === "drink"
            ? "2. Drink Tier Pricing (₹ / Day)"
            : planType === "meal_and_drink"
            ? "3. Combo Pricing (Meal Base + Drink Tiers in ₹ / Day)"
            : "2. Standard Meal Pricing (₹ / Day)"}
        </Typography>

        {/* Pure Drink Plan Pricing */}
        {planType === "drink" && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Coffee Explorer Tier (Base)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="2-Day Trial (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={coffeeTrial}
                  onChange={(e) => setCoffeeTrial(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Weekly 7-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={coffeeWeekly}
                  onChange={(e) => setCoffeeWeekly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Monthly 28-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={coffeeMonthly}
                  onChange={(e) => setCoffeeMonthly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Anchor MRP (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={coffeeNonOffer}
                  onChange={(e) => setCoffeeNonOffer(Number(e.target.value))}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Coffee & Blends Tier (+₹20-30 upgrade)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="2-Day Trial (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={blendTrial}
                  onChange={(e) => setBlendTrial(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Weekly 7-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={blendWeekly}
                  onChange={(e) => setBlendWeekly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Monthly 28-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={blendMonthly}
                  onChange={(e) => setBlendMonthly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Anchor MRP (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={blendNonOffer}
                  onChange={(e) => setBlendNonOffer(Number(e.target.value))}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Meal / Combo Pricing */}
        {planType !== "drink" && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {planType === "meal_and_drink" ? "Base Meal Classic + Coffee" : "Classic Meal Portion"}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="2-Day Trial (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Weekly 7-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={priceWeekly}
                  onChange={(e) => setPriceWeekly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Monthly 28-Day (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={priceMonthly}
                  onChange={(e) => setPriceMonthly(Number(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Anchor MRP (₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={priceNonOffer}
                  onChange={(e) => setPriceNonOffer(Number(e.target.value))}
                />
              </Grid>
            </Grid>

            {ifPortionsAvailable && (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {planType === "meal_and_drink" ? "Base Meal Light + Coffee" : "Light Meal Portion"}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Light Trial (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={priceLight}
                      onChange={(e) => setPriceLight(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Light Weekly (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={priceWeeklyLight}
                      onChange={(e) => setPriceWeeklyLight(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Light Monthly (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={priceMonthlyLight}
                      onChange={(e) => setPriceMonthlyLight(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Light Anchor MRP (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={priceNonOfferLight}
                      onChange={(e) => setPriceNonOfferLight(Number(e.target.value))}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {planType === "meal_and_drink" && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Drink Pricing Components (Used for Blend Upgrade Delta Calculation)
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Coffee Trial (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={coffeeTrial}
                      onChange={(e) => setCoffeeTrial(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Coffee Monthly (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={coffeeMonthly}
                      onChange={(e) => setCoffeeMonthly(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Blend Trial (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={blendTrial}
                      onChange={(e) => setBlendTrial(Number(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Blend Monthly (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={blendMonthly}
                      onChange={(e) => setBlendMonthly(Number(e.target.value))}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Section 4: Features */}
        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          {planType === "meal_and_drink" ? "4. Plan Highlights & Features" : "3. Plan Highlights & Features"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
          <TextField
            label="Add Feature Bullet"
            fullWidth
            size="small"
            value={newFeatureText}
            onChange={(e) => setNewFeatureText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddFeature();
              }
            }}
            placeholder="e.g. Free Daily Morning Delivery, Pause or Skip Days Anytime"
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddFeature}>
            Add
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {features.map((feat, idx) => (
            <Chip key={idx} label={feat} onDelete={() => handleRemoveFeature(idx)} color="default" />
          ))}
          {features.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No features added yet.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Section 5: Availability & Badges */}
        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          Availability & Visibility
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {isActive ? "Live in App (Active)" : "Draft Mode (Inactive)"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isActive ? "Visible to mobile customers" : "Hidden from mobile app list"}
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {isEnabled ? "Enabled for Purchase" : "Disabled"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Allows new subscriptions
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isBestseller}
                  onChange={(e) => setIsBestseller(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Bestseller Badge
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Highlights on browsing card
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Section 5: Plan Add-ons */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary">
              5. Plan Add-ons ({addOns.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Optional recurring items customers can add to this plan during customization
            </Typography>
          </Box>
          {!isAddingAddon && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddAddon}
            >
              Add Add-on
            </Button>
          )}
        </Box>

        {/* Add / Edit Add-on Inline Form Card */}
        {isAddingAddon && (
          <Box
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              border: "1.5px solid #E67E22",
              backgroundColor: "#FFFDF9",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: "#D97706" }}>
              {editingAddonIndex !== null ? "Edit Add-on" : "New Add-on"}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Add-on Name"
                  fullWidth
                  size="small"
                  value={addonName}
                  onChange={(e) => setAddonName(e.target.value)}
                  placeholder="e.g. Protein Cookie, Cold Brew Bottle"
                  required
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Price (₹ / day)"
                  type="number"
                  fullWidth
                  size="small"
                  value={addonPrice}
                  onChange={(e) => setAddonPrice(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="e.g. 40"
                  required
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Serving / Quantity"
                  fullWidth
                  size="small"
                  value={addonQuantity}
                  onChange={(e) => setAddonQuantity(e.target.value)}
                  placeholder="e.g. 50g, 1 piece"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={addonDescription}
                  onChange={(e) => setAddonDescription(e.target.value)}
                  placeholder="Brief description of the add-on nutritional benefit or ingredients"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label="Image URL (optional)"
                    fullWidth
                    size="small"
                    value={addonImage}
                    onChange={(e) => setAddonImage(e.target.value)}
                    placeholder="https://..."
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={uploadingAddonImage ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={uploadingAddonImage}
                    sx={{ whiteSpace: "nowrap", height: 40 }}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleUploadAddonImage}
                    />
                  </Button>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={addonIsActive}
                      onChange={(e) => setAddonIsActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={addonIsActive ? "Active (Visible)" : "Inactive"}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button size="small" onClick={handleCloseAddonEditor} color="inherit">
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveAddon}
                sx={{ backgroundColor: "#E67E22", "&:hover": { backgroundColor: "#D35400" } }}
              >
                {editingAddonIndex !== null ? "Update Add-on" : "Save Add-on"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Addons List */}
        {addOns.length === 0 && !isAddingAddon ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              borderRadius: 2,
              backgroundColor: "#F9FAFB",
              border: "1px dashed #D1D5DB",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No add-ons configured for this plan yet.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click "+ Add Add-on" to create optional items customers can add to their subscription.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {addOns.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #E5E7EB",
                  backgroundColor: item.isActive ? "#FFFFFF" : "#F9FAFB",
                  opacity: item.isActive ? 1 : 0.7,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                  {item.image ? (
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.name}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        objectFit: "cover",
                        border: "1px solid #E5E7EB",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        backgroundColor: "#FFF4EB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#E67E22",
                        fontWeight: 700,
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    >
                      ₹
                    </Box>
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" fontWeight={700} color="#18181B">
                        {item.name}
                      </Typography>
                      {item.quantity && (
                        <Chip
                          label={item.quantity}
                          size="small"
                          sx={{ height: 20, fontSize: 11, backgroundColor: "#F3F4F6" }}
                        />
                      )}
                      <Chip
                        label={`₹${item.price}/day`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                      />
                    </Box>
                    {item.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={item.isActive}
                        onChange={() => handleToggleAddonActive(index)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        {item.isActive ? "Active" : "Inactive"}
                      </Typography>
                    }
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenEditAddon(index)}
                    title="Edit Add-on"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAddon(index)}
                    title="Delete Add-on"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
        >
          {saving ? "Saving Plan..." : isEditing ? "Save Changes" : "Create Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanModal;
